import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum, breadcrumbTemplates } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { toast } from "react-toastify";
import NjdobiAdminService from "./njdobiAdminService";

export const ViewManifest: React.FC = () => {
  const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.ViewManifest];
  const dateInputRef = useRef<HTMLInputElement>(null);
  const facilityFileInputRef = useRef<HTMLInputElement>(null);
  const providerFileInputRef = useRef<HTMLInputElement>(null);
  const trainingFileInputRef = useRef<HTMLInputElement>(null);
  const [service] = useState(() => new NjdobiAdminService());
  const [endDate, setEndDate] = useState<string>("");
  const [facilityFile, setFacilityFile] = useState<File | null>(null);
  const [providerFile, setProviderFile] = useState<File | null>(null);
  const [trainingFile, setTrainingFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
  const [isUploadingTraining, setIsUploadingTraining] = useState(false);
  const [scheduleId, setScheduleId] = useState<number | null>(null);

  useEffect(() => {
    service.getSubmissionSchedules().then((schedules) => {
      const active = schedules
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];

      if (active) {
        setEndDate(active.endDate.length >= 10 ? active.endDate.substring(0, 10) : active.endDate);
        setScheduleId(active.submissionScheduleId);
      }
    }).catch(() => {
      toast.error("Failed to load submission schedule.", { containerId: "z-index-9999" });
    });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<File | null>>) => {
    const file = e.target.files?.[0] ?? null;
    setter(file);
  };

  const handleTemplateUpload = async () => {
    if (!facilityFile && !providerFile) return;
    if (!!facilityFile && facilityFile.name !== "NJDOBI-FacilityDataTemplate.xlsm") {
      toast.error("File must be named NJDOBI-FacilityDataTemplate.xlsm.", { containerId: "z-index-9999" });
      return;
    }
    if (!!providerFile && providerFile.name !== "NJDOBI-ProviderDataTemplate.xlsm") {
      toast.error("File must be named NJDOBI-ProviderDataTemplate.xlsm.", { containerId: "z-index-9999" });
      return;
    }
    setIsUploadingTemplate(true);
    try {
      if (!!facilityFile) {
        await service.uploadFacilityTemplate(facilityFile);
        toast.success("Facility template uploaded successfully.", { containerId: "z-index-9999" });
        setFacilityFile(null);
        if (facilityFileInputRef.current)
          facilityFileInputRef.current.value = "";
      }
    } catch {
      toast.error("Failed to upload facility template.", { containerId: "z-index-9999" });
    }

    try {
      if (!!providerFile) {
        await service.uploadProviderTemplate(providerFile);
        toast.success("Provider template uploaded successfully.", { containerId: "z-index-9999" });
        setProviderFile(null);
        if (providerFileInputRef.current)
          providerFileInputRef.current.value = "";
      }
    } catch {
      toast.error("Failed to upload provider template.", { containerId: "z-index-9999" });
    } finally {
      setIsUploadingTemplate(false);
    }
  };

  const handleTrainingUpload = async () => {
    if (!trainingFile) return;
    if (trainingFile.name !== "NJDOBIIssuerDataSubmissionTraining.pdf") {
      toast.error("File must be named NJDOBIIssuerDataSubmissionTraining.pdf.", { containerId: "z-index-9999" });
      return;
    }
    setIsUploadingTraining(true);
    try {
      await service.uploadTrainingFile(trainingFile);
      toast.success("Training file uploaded successfully.", { containerId: "z-index-9999" });
      setTrainingFile(null);
      if (trainingFileInputRef.current)
        trainingFileInputRef.current.value = "";
    } catch (err) {
      toast.error("Failed to upload training file.", { containerId: "z-index-9999" });
    } finally {
      setIsUploadingTraining(false);
    }
  };

  const handleSave = async () => {
    if (!scheduleId || !endDate) return;
    setIsSaving(true);
    try {
      await service.updateSubmissionScheduleEndDate(scheduleId, endDate);
      toast.success("End date updated successfully.", { containerId: "z-index-9999" });
    } catch {
      toast.error("Failed to save. Please try again.", { containerId: "z-index-9999" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Page documentTitle="View Manifest">
      <Breadcrumbs items={breadcrumbs} />
      <PageInner>
        <PageHeader title="View Manifest" disableHR={false} />
        <AccordionContainer className="mt-3">
          <Accordion title="NJDOBI Self-service functions" testId="view-manifest__njdobi-self-service-accordion">
            <div className="d-flex align-items-center justify-content-center mt-3">
              <span className="view-manifest__inline-label">Edit end date for submissions: </span>
              <div className="d-flex view-manifest__input-group">
                <QESDateInput
                  containerClassName="mb-0 view-manifest__date-input"
                  disabled={isSaving}
                  hideLabel
                  inputAppend={
                    <QESButton
                      className="view-manifest__update-btn"
                      disabled={isSaving || !endDate || !scheduleId}
                      onClick={handleSave}
                      qesTestId="view-manifest__njdobi-update-end-date"
                      qesType="primary"
                    >
                      Update
                    </QESButton>
                  }
                  inputPrepend={
                    <QESButton
                      aria-label="Open date picker"
                      className="view-manifest__calendar-prepend"
                      onClick={() => dateInputRef.current?.showPicker()}
                      qesType="secondary"
                      qesTestId="view-manifest__open-date-picker"
                    >
                      <QesIcon iconName={QesIcons.Calendar} isDecorative />
                    </QESButton>
                  }
                  label="Edit end date for submissions"
                  onChange={(e) => setEndDate(e.target.value)}
                  qesRef={dateInputRef}
                  testId="view-manifest__njdobi-end-date"
                  value={endDate}
                />
              </div>
            </div>

            <input
              accept=".xlsm"
              className="view-manifest__file-input"
              onChange={(e) => handleFileChange(e, setFacilityFile)}
              ref={facilityFileInputRef}
              id="facility-file"
              type="file"
            />
            <input
              accept=".xlsm"
              className="view-manifest__file-input"
              onChange={(e) => handleFileChange(e, setProviderFile)}
              ref={providerFileInputRef}
              id="provider-file"
              type="file"
            />
            <div className="mt-3">
              <p><strong>Update NJDOBI Excel template files:</strong></p>
              <div className="ms-3">
                <p>
                  This upload will replace the NJDOBI provider and facility template files which are
                  downloadable from{" "}
                  <a href={`${window.uiEndpoint}/njdobi`} rel="noreferrer" target="_blank">
                    New Jersey DOBI - Provider Data Requirements.
                  </a>{" "}
                  The files must be named exactly the same as the following:
                </p>
                <p>
                  <strong>
                    NJDOBI-FacilityDataTemplate.xlsm
                  </strong>{" "}
                  for the facility template
                </p>
                <p>
                  <strong>
                    NJDOBI-ProviderDataTemplate.xlsm
                  </strong>{" "}
                  for the provider template
                </p>
              </div>
            </div>

            <div className="form-group row ms-3">
              <div className="col-sm-6 ps-0">
                <QESTextInput
                  containerClassName="mb-0 view-manifest__facility-input"
                  disabled={isUploadingTemplate}
                  inputAppend={
                    <QESButton
                      className="view-manifest__browse-btn"
                      disabled={isUploadingTemplate}
                      onClick={() => facilityFileInputRef.current?.click()}
                      qesTestId="view-manifest__njdobi-browse-facility"
                      qesType="outline"
                    >
                      <QesIcon iconName={QesIcons.Folder} isDecorative /> Browse
                    </QESButton>
                  }
                  label="Facility template"
                  placeholder="Select a Facility file..."
                  readOnly
                  testId="view-manifest__njdobi-facility-file"
                  id="view-manifest__njdobi-facility-file"
                  value={facilityFile?.name ?? ""}
                />
              </div>

              <div className="col-sm-6">
                <QESTextInput
                  containerClassName="mb-0 view-manifest__provider-input"
                  disabled={isUploadingTemplate}
                  inputAppend={
                    <QESButton
                      className="view-manifest__browse-btn"
                      disabled={isUploadingTemplate}
                      onClick={() => providerFileInputRef.current?.click()}
                      qesTestId="view-manifest__njdobi-browse-provider"
                      qesType="outline"
                    >
                      <QesIcon iconName={QesIcons.Folder} isDecorative /> Browse
                    </QESButton>
                  }
                  label="Provider template"
                  placeholder="Select a Provider file..."
                  readOnly
                  testId="view-manifest__njdobi-provider-file"
                  id="view-manifest__njdobi-provider-file"
                  value={providerFile?.name ?? ""}
                />
              </div>
            </div>

            <div className="row ms-3">
              <div className="ps-0">
                <QESButton
                  className="view-manifest__upload-btn"
                  disabled={isUploadingTemplate || (!facilityFile && !providerFile)}
                  onClick={handleTemplateUpload}
                  qesTestId="view-manifest__njdobi-upload-template"
                  qesType="primary"
                >
                  <QesIcon iconName={QesIcons.UploadV1} isDecorative /> Upload
                </QESButton>
              </div>
            </div>

            <input
              accept=".pdf"
              className="view-manifest__file-input"
              onChange={(e) => handleFileChange(e, setTrainingFile)}
              ref={trainingFileInputRef}
              id="training-file"
              type="file"
            />

            <div className="mt-3">
              <p><strong>Update NJDOBI PDF training file:</strong></p>
              <div className="ms-3">
                <p>
                  This upload will replace the existing {" "}
                  <strong>
                    NJDOBIIssuerDataSubmissionTraining.pdf
                  </strong>{" "}
                  file. The file must be named exactly the same to upload.
                </p>
              </div>
            </div>
            <div className="form-group row ms-3">
              <div className="col-sm-6 ps-0">
                <QESTextInput
                  containerClassName="mb-0 view-manifest__training-input"
                  disabled={isUploadingTraining}
                  hideLabel
                  inputAppend={
                    <QESButton
                      className="view-manifest__browse-btn"
                      disabled={isUploadingTraining}
                      onClick={() => trainingFileInputRef.current?.click()}
                      qesTestId="view-manifest__njdobi-browse-training"
                      qesType="outline"
                    >
                      <QesIcon iconName={QesIcons.Folder} isDecorative /> Browse
                    </QESButton>
                  }
                  label="New NJDOBI Training File"
                  placeholder="Select a file..."
                  readOnly
                  testId="view-manifest__njdobi-training-file"
                  value={trainingFile?.name ?? ""}
                />
              </div>
            </div>
            <div className="row ms-3">
              <div className="ps-0">
                <QESButton
                  className="view-manifest__upload-btn"
                  disabled={isUploadingTraining || (!trainingFile)}
                  onClick={handleTrainingUpload}
                  qesTestId="view-manifest__njdobi-upload-training"
                  qesType="primary"
                >
                  <QesIcon iconName={QesIcons.UploadV1} isDecorative /> Upload
                </QESButton>
              </div>
            </div>
          </Accordion>
        </AccordionContainer>
      </PageInner>
    </Page>
  );
};
